// src/features/clients/components/InfluencerCodeManager.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { clientsApi } from '../../../api/clientsApi';
import { BaseApiError } from '../../../api/baseClient';
import { toast } from 'react-hot-toast';
import { Hash, X, Check, Loader2, AlertTriangle, Trash2, Lock, Crown } from 'lucide-react';
import { isPro as checkIsPremium } from '../../../utils/planPermissions';
import ShareReferralCard from './ShareReferralCard';

const REFERRAL_CODE_REGEX = /^[A-Z0-9-]{3,15}$/;

interface InfluencerCodeManagerProps {
  clientId: string;
  /** Teléfono del cliente — identificador de referido por defecto (si no hay código). */
  clientPhone?: string | null;
}

const InfluencerCodeManager: React.FC<InfluencerCodeManagerProps> = ({ clientId, clientPhone }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [editingReferralCode, setEditingReferralCode] = useState(false);
  const [referralCodeInput, setReferralCodeInput] = useState('');
  const [referralCodeLoading, setReferralCodeLoading] = useState(false);
  const [referralCodeError, setReferralCodeError] = useState('');

  const isAdmin = currentUser?.role === 'admin';
  const hasPremiumAccess = checkIsPremium(currentUser?.subscription) || isAdmin;
  const referralsEnabled = currentUser?.referralsEnabled || false;

  const fetchReferralCode = useCallback(async () => {
    if (!clientId) return;
    try {
      const c = await clientsApi.get(clientId);
      setReferralCode(c.referralCode || null);
    } catch (error) {
      console.error('Error fetching referral code:', error);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId && hasPremiumAccess) {
      fetchReferralCode();
    }
  }, [clientId, hasPremiumAccess, fetchReferralCode]);

  const handleSaveReferralCode = async () => {
    if (!clientId || !currentUser?.businessId || referralCodeLoading) return;

    const code = referralCodeInput.trim().toUpperCase();

    if (!code) {
      setReferralCodeError('Ingresa un código');
      return;
    }
    if (!REFERRAL_CODE_REGEX.test(code)) {
      setReferralCodeError('3-15 caracteres: solo letras, números y guiones');
      return;
    }

    setReferralCodeLoading(true);
    setReferralCodeError('');

    try {
      // base impone unicidad del código por tenant (UNIQUE) → 409 si ya está en uso.
      await clientsApi.setReferralCode(clientId, code);

      setReferralCode(code);
      setEditingReferralCode(false);
      setReferralCodeInput('');
      setReferralCodeError('');
      toast.success(`Código ${code} asignado correctamente`);
    } catch (error) {
      if (error instanceof BaseApiError && error.status === 409) {
        setReferralCodeError('Este código ya está asignado a otro cliente');
      } else {
        console.error('Error saving referral code:', error);
        toast.error('Error al guardar el código');
      }
    } finally {
      setReferralCodeLoading(false);
    }
  };

  const handleRemoveReferralCode = async () => {
    if (!clientId || !currentUser?.businessId || referralCodeLoading) return;

    setReferralCodeLoading(true);

    try {
      await clientsApi.removeReferralCode(clientId);

      setReferralCode(null);
      setEditingReferralCode(false);
      setReferralCodeInput('');
      setReferralCodeError('');
      toast.success('Código eliminado');
    } catch (error) {
      console.error('Error removing referral code:', error);
      toast.error('Error al eliminar el código');
    } finally {
      setReferralCodeLoading(false);
    }
  };

  if (!hasPremiumAccess) {
    return (
      <div className="mt-3 pt-3 border-t border-line">
        <button
          onClick={() => navigate('/dashboard/subscription')}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-content-3 bg-surface-2 border border-line hover:bg-yellow-50 dark:hover:bg-yellow-500/10 hover:text-yellow-700 dark:text-yellow-300 hover:border-yellow-200 transition-colors group"
        >
          <Lock className="h-3.5 w-3.5 mr-1.5 text-content-3 group-hover:text-yellow-500" />
          Código de influencer
          <Crown className="h-3 w-3 ml-1.5 text-content-3 group-hover:text-yellow-500" />
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-line">
      {!editingReferralCode ? (
        <div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Hash className="h-3.5 w-3.5 text-purple-500 flex-shrink-0" />
              {referralCode ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-content-2">Código influencer:</span>
                  <span className="px-2.5 py-0.5 bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 text-sm font-mono font-semibold rounded-md border border-purple-200">
                    {referralCode}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-content-3">Sin código de influencer</span>
              )}
            </div>
            <button
              onClick={() => {
                setEditingReferralCode(true);
                setReferralCodeInput(referralCode || '');
                setReferralCodeError('');
              }}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:text-purple-300 font-medium transition-colors"
            >
              {referralCode ? 'Editar' : 'Asignar código'}
            </button>
          </div>

          {referralCode && !referralsEnabled && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded-md border border-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                El sistema de referidos está desactivado. Actívalo desde{' '}
                <button onClick={() => navigate('/dashboard/referrals?tab=ajustes')} className="underline font-medium hover:text-amber-900">
                  Referidos → Ajustes
                </button>{' '}
                para que este código funcione.
              </span>
            </div>
          )}

          {/* Tarjeta de invitación compartible (fondo IA + texto por canvas) */}
          <ShareReferralCard code={referralCode} phone={clientPhone} />
        </div>
      ) : (
        <div className="bg-purple-50 dark:bg-purple-500/10 rounded-lg p-4 border border-purple-200 animate-fade-in-up">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-purple-800 dark:text-purple-300 flex items-center">
              <Hash className="h-4 w-4 mr-1.5" />
              Código de Influencer
            </h4>
            <button
              onClick={() => { setEditingReferralCode(false); setReferralCodeError(''); }}
              className="text-content-3 hover:text-content-2 p-1 rounded-full hover:bg-surface transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={referralCodeInput}
              onChange={(e) => {
                const sanitized = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
                setReferralCodeInput(sanitized);
                setReferralCodeError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && referralCodeInput.trim()) {
                  handleSaveReferralCode();
                }
              }}
              placeholder="Ej: DOME100"
              maxLength={15}
              autoFocus
              className={`flex-1 border rounded-md px-3 py-2 text-sm font-mono uppercase focus:ring-purple-500 focus:border-purple-500 ${
                referralCodeError ? 'border-red-300 bg-red-50 dark:bg-red-500/10' : 'border-line'
              }`}
            />
            <button
              onClick={handleSaveReferralCode}
              disabled={referralCodeLoading || !referralCodeInput.trim()}
              className="inline-flex items-center px-4 py-2 text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {referralCodeLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
            </button>
          </div>

          {referralCodeError && (
            <p className="text-xs text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3 flex-shrink-0" />
              {referralCodeError}
            </p>
          )}

          <p className="text-xs text-purple-500">
            3-15 caracteres: letras, números y guiones. Se guarda en mayúsculas.
          </p>

          {referralCode && (
            <button
              onClick={handleRemoveReferralCode}
              disabled={referralCodeLoading}
              className="mt-2 text-xs text-red-500 hover:text-red-700 dark:text-red-300 flex items-center transition-colors disabled:opacity-50"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Eliminar código
            </button>
          )}

          {!referralsEnabled && (
            <div className="mt-3 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 p-2 rounded-md border border-amber-200">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>
                El sistema de referidos está desactivado. Actívalo desde{' '}
                <button onClick={() => navigate('/dashboard/referrals?tab=ajustes')} className="underline font-medium hover:text-amber-900">
                  Referidos → Ajustes
                </button>{' '}
                para que los códigos funcionen.
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InfluencerCodeManager;